/**
 * Floating chat widget, rendered site-wide from snippets/chat-widget.liquid
 * (included in layout/theme.liquid). Talks to the LangGraph chatbot backend
 * (backend/app/routers/chatbot.py, POST /chatbot/chat) -- same
 * fetch/IIFE/plain-DOM style as photo-upload-widget.js, no framework.
 *
 * Conversation history + open/closed state live in localStorage (device-wide,
 * not per-tab) so clicking a recommendation link -- which opens in a new tab
 * via target="_blank" -- doesn't reset the conversation: sessionStorage was
 * tried first, but rel="noopener" (needed against reverse-tabnabbing) also
 * blocks the browser from cloning sessionStorage into that new tab. History
 * self-expires after HISTORY_MAX_AGE_MS of inactivity so nothing sensitive
 * sits there indefinitely. user_id is a random id kept in localStorage across
 * sessions; the backend doesn't branch on it today, it's just there for
 * possible future analytics.
 */
(function () {
  const BACKEND_CHAT_URL = "https://ronys-backend-144174532283.us-central1.run.app/chatbot/chat";
  const HISTORY_KEY = "ronysChatHistory";
  const OPEN_KEY = "ronysChatOpen";
  const USER_ID_KEY = "ronysChatUserId";
  const MAX_HISTORY_TURNS = 20;
  const HISTORY_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h of inactivity -> fresh chat

  const bubble = document.getElementById("RonysChatBubble");
  const panel = document.getElementById("RonysChatPanel");
  const closeButton = document.getElementById("RonysChatClose");
  const messagesEl = document.getElementById("RonysChatMessages");
  const form = document.getElementById("RonysChatForm");
  const input = document.getElementById("RonysChatInput");
  const sendButton = document.getElementById("RonysChatSend");

  if (!bubble || !panel || !form || !input) return;

  const t = window.chatWidgetStrings || {};

  function getUserId() {
    try {
      let id = localStorage.getItem(USER_ID_KEY);
      if (!id) {
        id = (crypto.randomUUID && crypto.randomUUID()) || `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(USER_ID_KEY, id);
      }
      return id;
    } catch (err) {
      return "guest";
    }
  }

  function loadHistory() {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed.updatedAt || Date.now() - parsed.updatedAt > HISTORY_MAX_AGE_MS) {
        localStorage.removeItem(HISTORY_KEY);
        return [];
      }
      return parsed.turns || [];
    } catch (err) {
      return [];
    }
  }

  function saveHistory(history) {
    try {
      const turns = history.slice(-MAX_HISTORY_TURNS);
      localStorage.setItem(HISTORY_KEY, JSON.stringify({ turns, updatedAt: Date.now() }));
    } catch (err) {
      // localStorage unavailable (private mode, quota) -- history just won't
      // survive a page navigation; the widget still works within the page.
    }
  }

  let history = loadHistory();

  function scrollToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Bot replies come back as plain text with a tiny bit of Markdown (the
  // prompts in backend/app/chatbot/graph/prompts.py ask for **bold** and
  // [label](url) links, nothing fancier) -- escape first so nothing in the
  // LLM output can inject markup, THEN turn just those two constructs into
  // real HTML. Links are restricted to http(s) so a crafted reply can't
  // sneak in a javascript: URL.
  function renderBotContent(text) {
    let html = escapeHtml(text);
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    return html;
  }

  function renderMessage(role, content) {
    const bubbleEl = document.createElement("div");
    bubbleEl.className = `ronys-chat-message ronys-chat-message--${role}`;
    if (role === "bot") {
      bubbleEl.innerHTML = renderBotContent(content);
    } else {
      bubbleEl.textContent = content;
    }
    messagesEl.appendChild(bubbleEl);
    scrollToBottom();
    return bubbleEl;
  }

  function renderTyping() {
    const typingEl = document.createElement("div");
    typingEl.className = "ronys-chat-typing";
    typingEl.id = "RonysChatTyping";
    typingEl.innerHTML = "<span></span><span></span><span></span>";
    messagesEl.appendChild(typingEl);
    scrollToBottom();
  }

  function removeTyping() {
    const typingEl = document.getElementById("RonysChatTyping");
    if (typingEl) typingEl.remove();
  }

  function renderHistory() {
    messagesEl.innerHTML = "";
    if (history.length === 0 && t.greeting) {
      renderMessage("bot", t.greeting);
      return;
    }
    for (const turn of history) {
      renderMessage(turn.role === "user" ? "user" : "bot", turn.content);
    }
  }

  function setOpen(open) {
    panel.hidden = !open;
    bubble.setAttribute("aria-expanded", open ? "true" : "false");
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch (err) {
      // ignore -- non-critical
    }
    if (open) {
      input.focus();
      scrollToBottom();
    }
  }

  bubble.addEventListener("click", () => {
    setOpen(panel.hidden);
  });

  if (closeButton) {
    closeButton.addEventListener("click", () => setOpen(false));
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const message = input.value.trim();
    if (!message || sendButton.disabled) return;

    input.value = "";
    renderMessage("user", message);
    sendButton.disabled = true;
    renderTyping();

    try {
      const response = await fetch(BACKEND_CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: getUserId(),
          message,
          history,
          language: document.documentElement.lang || "es",
        }),
      });

      removeTyping();

      if (!response.ok) {
        renderMessage(
          "error",
          response.status === 429 ? (t.limitMessage || "") : (t.errorMessage || "")
        );
        return;
      }

      const data = await response.json();
      const reply = data.reply || "";
      history.push({ role: "user", content: message });
      history.push({ role: "model", content: reply });
      saveHistory(history);
      renderMessage("bot", reply);
    } catch (err) {
      removeTyping();
      renderMessage("error", t.errorMessage || "");
    } finally {
      sendButton.disabled = false;
    }
  });

  renderHistory();
  let wasOpen = false;
  try {
    wasOpen = localStorage.getItem(OPEN_KEY) === "1";
  } catch (err) {
    wasOpen = false;
  }
  setOpen(wasOpen);
})();
